/**
 * Builds the network-prefix tree that `Networks.tsx` renders.
 *
 * The backend's `depth` field reflects NetBox's full tree, including
 * ancestors that may be invisible to the current user (owner filtering can
 * drop arbitrary intermediate prefixes). We therefore ignore `depth` and
 * derive parent-child relationships purely from CIDR containment, which is
 * structurally accurate regardless of which rows are present.
 *
 * Sibling indexing and the initial expanded set are computed here too so the
 * component only deals with rendering and interaction state.
 */
import type { Network } from '../../../types/models/Network';
import { type ParsedCidr, cidrContains, compareCidr, parseCidr } from '../../../utils/cidr';

export interface NetworkTreeRow {
  network: Network;
  parsed: ParsedCidr;
  /** Depth in the displayed tree; 0 = displayed root. */
  effectiveDepth: number;
  /** `null` for displayed roots, else the parent row's `network.id`. */
  parentId: number | null;
  /** 1-based position among siblings sharing the same `parentId`. */
  posinset: number;
}

const FALLBACK_FAMILY: 4 | 6 = 4;

function safeParse(network: Network): ParsedCidr | null {
  try {
    return parseCidr(network.prefix);
  } catch {
    return null;
  }
}

interface ParsedRow {
  network: Network;
  parsed: ParsedCidr;
}

/**
 * Build tree rows from a flat list. Returned rows are in canonical tree order
 * (parents before children, siblings in CIDR order), which is the order
 * `Networks.tsx` renders them in.
 */
export function buildNetworkTree(networks: Network[]): NetworkTreeRow[] {
  // 1. Parse + filter malformed prefixes.
  const parsed: ParsedRow[] = [];
  for (const n of networks) {
    const p = safeParse(n);
    if (p === null) {
      continue;
    }
    // The backend types `family` as `number | null`. Honour an explicit family
    // if present, otherwise trust the parsed value (and fall back to IPv4 if
    // even that's missing — should never happen but cheap to guard).
    const family = n.family === 4 || n.family === 6 ? n.family : p.family || FALLBACK_FAMILY;
    parsed.push({
      network: n,
      parsed: { ...p, family },
    });
  }

  if (parsed.length === 0) {
    return [];
  }

  // 2. Sort: family asc, then network asc, then mask asc. This ordering
  //    guarantees that any prefix's ancestors are visited before it.
  parsed.sort((a, b) => compareCidr(a.parsed, b.parsed));

  // 3. Walk forward with a stack of "currently open" ancestors. For each row,
  //    pop the stack until the top strictly contains the row — that top is
  //    the parent. If the stack empties, the row is a displayed root.
  //
  //    This works regardless of NetBox's reported `depth` and correctly
  //    handles owner-filtered datasets where intermediate ancestors are
  //    missing (the visible "parent" may sit several NetBox levels above
  //    its visible child, but it's still the structural parent we want).
  const rows: NetworkTreeRow[] = [];
  const stack: NetworkTreeRow[] = [];

  for (const r of parsed) {
    while (stack.length > 0) {
      const top = stack[stack.length - 1];
      if (
        top.parsed.family === r.parsed.family &&
        top.parsed.mask < r.parsed.mask &&
        cidrContains(top.parsed, r.parsed)
      ) {
        break;
      }
      stack.pop();
    }

    const parent = stack.length > 0 ? stack[stack.length - 1] : null;
    const row: NetworkTreeRow = {
      network: r.network,
      parsed: r.parsed,
      effectiveDepth: parent ? parent.effectiveDepth + 1 : 0,
      parentId: parent ? parent.network.id : null,
      posinset: 0,
    };
    rows.push(row);
    stack.push(row);
  }

  // 4. Sibling indexing — assign 1-based position among siblings.
  const cursor = new Map<number | null, number>();
  for (const row of rows) {
    const next = (cursor.get(row.parentId) ?? 0) + 1;
    cursor.set(row.parentId, next);
    row.posinset = next;
  }

  return rows;
}

/**
 * Set of row ids that are parents of any displayed row. Used as the default
 * initial value of `expandedIds` so the entire tree is visible on first load;
 * users can still collapse subtrees manually.
 */
export function defaultExpandedIds(rows: NetworkTreeRow[]): Set<number> {
  const ids = new Set<number>();
  for (const r of rows) {
    if (r.parentId !== null) {
      ids.add(r.parentId);
    }
  }
  return ids;
}

/**
 * Lookup index: `parentId → ancestor chain (root → immediate parent)`.
 * Used to compute per-row visibility and "expand all ancestors of a match"
 * for filtered views without re-walking the rows array each render.
 */
export function buildAncestorIndex(rows: NetworkTreeRow[]): Map<number, number[]> {
  const byId = new Map<number, NetworkTreeRow>();
  for (const r of rows) {
    byId.set(r.network.id, r);
  }
  const ancestors = new Map<number, number[]>();
  for (const r of rows) {
    const chain: number[] = [];
    let cursor: number | null = r.parentId;
    while (cursor !== null) {
      chain.unshift(cursor);
      const next = byId.get(cursor);
      cursor = next ? next.parentId : null;
    }
    ancestors.set(r.network.id, chain);
  }
  return ancestors;
}
